/**
 * Hook for calculating and caching user karma
 */
import { useState, useCallback, useEffect } from 'react';
import type { DAGStore } from '@amorphous/core';

interface UseKarmaResult {
  getKarma: (pubkey: string) => number;
  getUserStats: (pubkey: string) => Promise<{
    karma: number;
    profileAge: number;
    articleCount: number;
    commentCount: number;
  }>;
  refreshKarma: (pubkey: string) => Promise<number>;
  myKarma: number;
}

export function useKarma(
  store: DAGStore | null,
  myPubkey: string | null
): UseKarmaResult {
  const [karmaCache, setKarmaCache] = useState<Map<string, number>>(new Map());
  const [myKarma, setMyKarma] = useState(0);

  // Load own karma on mount
  useEffect(() => {
    if (store && myPubkey) {
      store.getKarma(myPubkey).then(setMyKarma);
    }
  }, [store, myPubkey]);

  const getKarma = useCallback((pubkey: string): number => {
    return karmaCache.get(pubkey) ?? 0;
  }, [karmaCache]);

  const refreshKarma = useCallback(async (pubkey: string): Promise<number> => {
    if (!store) return 0;
    const karma = await store.getKarma(pubkey);
    setKarmaCache(prev => {
      const next = new Map(prev);
      next.set(pubkey, karma);
      return next;
    });
    if (pubkey === myPubkey) {
      setMyKarma(karma);
    }
    return karma;
  }, [store, myPubkey]);

  const getUserStats = useCallback(async (pubkey: string) => {
    if (!store) {
      return { karma: 0, profileAge: 0, articleCount: 0, commentCount: 0 };
    }
    return store.getUserStats(pubkey);
  }, [store]);

  return { getKarma, getUserStats, refreshKarma, myKarma };
}
